import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { usersApi, rolesApi } from '../services/api';
import './UserEdit.css';

const VIP_DURATION_OPTIONS = [
  { value: '1m', label: '1 Month' },
  { value: '3m', label: '3 Months' },
  { value: '6m', label: '6 Months' },
  { value: '1y', label: '1 Year' },
  { value: 'lifetime', label: 'Lifetime' },
];

const ROLE_COLORS = {
  player:      { bg: 'rgba(52, 152, 219, 0.15)',  color: '#3498db', label: 'Player' },
  dealer:      { bg: 'rgba(39, 174, 96, 0.15)',   color: '#27ae60', label: 'Dealer' },
  director:    { bg: 'rgba(243, 156, 18, 0.15)',  color: '#f39c12', label: 'Director' },
  leagueAdmin: { bg: 'rgba(102, 126, 234, 0.15)', color: '#667eea', label: 'League Admin' },
  siteAdmin:   { bg: 'rgba(192, 57, 43, 0.15)',   color: '#c0392b', label: 'Site Admin' },
};

function formatPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function UserEdit({ user: loggedInUser, onLogout }) {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    phoneNumber: '',
    email: '',
    password: '',
    vip1: false,
    vipDuration: '1m',
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [playerStats, setPlayerStats] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([
        usersApi.getAll(),
        rolesApi.getAll(),
      ]);

      const users = usersRes?.data?.users || usersRes?.users || [];
      const foundUser = users.find(u => String(u.id) === String(userId));

      if (!foundUser) {
        setError('User not found');
        return;
      }

      setUserData(foundUser);
      setFormData({
        phoneNumber: foundUser.phoneNumber || '',
        email: foundUser.email || '',
        password: '',
        vip1: foundUser.vip1 || false,
        vipDuration: foundUser.vipExpiration ? 'existing' : 'lifetime',
      });
      setSelectedRoles(foundUser.roles || []);

      const roles = rolesRes?.data?.roles || rolesRes?.roles || [];
      setAvailableRoles(roles);

      // Fetch player stats
      try {
        const statsRes = await usersApi.getStats(userId);
        setPlayerStats(statsRes.data || statsRes);
      } catch (e) { /* stats endpoint may not exist yet */ }
    } catch (err) {
      setError('Failed to load user data');
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVipToggle = () => {
    setFormData(prev => ({
      ...prev,
      vip1: !prev.vip1,
      vipDuration: !prev.vip1 ? '1m' : prev.vipDuration,
    }));
  };

  const handleRoleToggle = (roleName) => {
    setSelectedRoles(prev =>
      prev.includes(roleName)
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build update payload (only changed fields)
      const updatePayload = {};

      if (formData.phoneNumber !== (userData.phoneNumber || '')) {
        updatePayload.phoneNumber = formData.phoneNumber;
      }
      if (formData.email !== (userData.email || '')) {
        updatePayload.email = formData.email;
      }
      if (formData.vip1 !== (userData.vip1 || false)) {
        updatePayload.vip1 = formData.vip1;
        if (formData.vip1) {
          updatePayload.vipDuration = formData.vipDuration;
        }
      } else if (formData.vip1 && formData.vipDuration !== 'existing') {
        // VIP was already on but admin changed the duration
        updatePayload.vip1 = true;
        updatePayload.vipDuration = formData.vipDuration;
      }
      if (formData.password && formData.password.trim()) {
        updatePayload.password = formData.password;
      }

      // Check if roles changed
      const currentRoles = (userData.roles || []).sort().join(',');
      const newRoles = selectedRoles.sort().join(',');
      const rolesChanged = currentRoles !== newRoles;

      // Update profile fields if any changed
      if (Object.keys(updatePayload).length > 0) {
        const res = await usersApi.update(userId, updatePayload);
        if (res?.error) {
          throw new Error(res.error);
        }
      }

      // Update roles if changed
      if (rolesChanged) {
        const rolesRes = await usersApi.updateRoles(userId, selectedRoles);
        if (rolesRes?.error) {
          throw new Error(rolesRes.error);
        }
      }

      if (Object.keys(updatePayload).length === 0 && !rolesChanged) {
        showToast('success', 'No changes to save');
      } else {
        showToast('success', 'User updated successfully');
        // Refresh data from server
        await fetchUserData();
      }
    } catch (err) {
      showToast('error', err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar user={loggedInUser} onLogout={onLogout} />
      <div style={{ flex: 1, marginLeft: 250, padding: '24px 32px', background: '#f5f7fa' }}>
        {/* Toast */}
        {toast && (
          <div className={`user-edit-toast ${toast.type}`}>
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="user-edit-header">
          <button className="user-edit-back-btn" onClick={handleCancel}>
            ← Back
          </button>
          <h1 className="user-edit-title">Edit User</h1>
          {userData && (
            <span className="user-edit-username">@{userData.username}</span>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="user-edit-loading">Loading user data...</div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="user-edit-error">
            <span>{error}</span>
            <button className="user-edit-back-btn" onClick={handleCancel}>
              Back to Users
            </button>
          </div>
        )}

        {/* Edit Form */}
        {userData && !loading && !error && (
          <>
            {/* Read-only Info */}
            <div className="user-edit-card">
              <h3>User Info</h3>
              <div className="user-edit-grid">
                <div className="user-edit-field">
                  <label>First Name</label>
                  <div className="readonly-value">{userData.firstName}</div>
                </div>
                <div className="user-edit-field">
                  <label>Last Name</label>
                  <div className="readonly-value">{userData.lastName}</div>
                </div>
                <div className="user-edit-field">
                  <label>Username</label>
                  <div className="readonly-value" style={{ fontFamily: 'monospace' }}>
                    {userData.username}
                  </div>
                </div>
                <div className="user-edit-field">
                  <label>Created</label>
                  <div className="readonly-value" style={{ fontSize: 14, color: '#666' }}>
                    {userData.createdAt
                      ? new Date(userData.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })
                      : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Editable Fields */}
            <div className="user-edit-card">
              <h3>Edit Details</h3>
              <div className="user-edit-grid">
                <div className="user-edit-field">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="e.g. 3035551234"
                  />
                  {formData.phoneNumber && (
                    <span className="field-hint">
                      Display: {formatPhone(formData.phoneNumber)}
                    </span>
                  )}
                </div>
                <div className="user-edit-field">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="user-edit-field">
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Leave blank to keep current"
                  />
                  <span className="field-hint">Min 4 characters</span>
                </div>
              </div>
            </div>

            {/* VIP Status */}
            <div className="user-edit-card">
              <h3>VIP Status</h3>
              <div className="user-edit-vip-toggle">
                <label className="vip-switch">
                  <input
                    type="checkbox"
                    checked={formData.vip1}
                    onChange={handleVipToggle}
                  />
                  <span className="vip-slider"></span>
                </label>
                <span className="vip-label">
                  {formData.vip1 ? '⭐ VIP Active' : 'Not VIP'}
                </span>
              </div>

              {formData.vip1 && (
                <div style={{ marginTop: 16 }}>
                  <div className="user-edit-field">
                    <label>VIP Duration</label>
                    <select
                      value={formData.vipDuration === 'existing' ? '' : formData.vipDuration}
                      onChange={e => setFormData(prev => ({ ...prev, vipDuration: e.target.value }))}
                    >
                      {formData.vipDuration === 'existing' && (
                        <option value="" disabled>Keep current expiration</option>
                      )}
                      {VIP_DURATION_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {userData.vipExpiration && (
                    <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                      Expires:{' '}
                      <span style={{
                        fontWeight: 600,
                        color: new Date(userData.vipExpiration) > new Date() ? '#27ae60' : '#e74c3c',
                      }}>
                        {new Date(userData.vipExpiration).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                        {new Date(userData.vipExpiration) <= new Date() && ' (Expired)'}
                      </span>
                    </div>
                  )}
                  {!userData.vipExpiration && userData.vip1 && (
                    <div style={{ marginTop: 8, fontSize: 13, color: '#27ae60', fontWeight: 600 }}>
                      Lifetime VIP
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Roles */}
            <div className="user-edit-card">
              <h3>Roles</h3>
              <div className="user-edit-roles">
                {availableRoles.map(role => {
                  const roleStyle = ROLE_COLORS[role.name] || {
                    bg: 'rgba(127,140,141,0.15)',
                    color: '#7f8c8d',
                    label: role.name,
                  };
                  const isSelected = selectedRoles.includes(role.name);
                  return (
                    <label
                      key={role.name}
                      className="role-checkbox"
                      style={{
                        background: isSelected ? roleStyle.bg : '#f5f5f5',
                        color: isSelected ? roleStyle.color : '#999',
                        borderColor: isSelected ? roleStyle.color : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRoleToggle(role.name)}
                        style={{ accentColor: roleStyle.color }}
                      />
                      <span>{roleStyle.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="user-edit-actions">
              <button
                className="user-edit-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="user-edit-cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {/* Player Stats Section */}
        {playerStats && (
          <div style={{ marginTop: '30px' }}>
            {/* Stats Summary */}
            <h3 style={{ color: '#1a1a2e', marginBottom: '16px' }}>Player Stats</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
              {[
                { label: 'Total Points', value: playerStats.summary?.totalPoints || 0, icon: '⭐' },
                { label: 'Games Played', value: playerStats.summary?.gamesPlayed || 0, icon: '🎴' },
                { label: 'Wins', value: playerStats.summary?.wins || 0, icon: '🏆' },
                { label: 'Top 3', value: playerStats.summary?.top3 || 0, icon: '🥇' },
                { label: 'Best Finish', value: playerStats.summary?.bestFinish ? `#${playerStats.summary.bestFinish}` : '-', icon: '📈' },
              ].map((stat, i) => (
                <div key={i} style={{ background: '#f8f9fa', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px' }}>{stat.icon}</div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a2e' }}>{stat.value}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* League Standings */}
            {(playerStats.leagueStats || []).length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#1a1a2e', marginBottom: '12px' }}>League Standings</h4>
                <table className="data-table" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr><th>League</th><th>Points</th><th>Games</th><th>Best</th></tr>
                  </thead>
                  <tbody>
                    {playerStats.leagueStats.map(ls => (
                      <tr key={ls.leagueId}>
                        <td><strong>{ls.leagueName}</strong></td>
                        <td>{ls.totalPoints}</td>
                        <td>{ls.gamesPlayed}</td>
                        <td>#{ls.bestFinish}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recent Games */}
            {(playerStats.recentGames || []).length > 0 && (
              <div>
                <h4 style={{ color: '#1a1a2e', marginBottom: '12px' }}>Recent Games</h4>
                <table className="data-table" style={{ fontSize: '14px' }}>
                  <thead>
                    <tr><th>Date</th><th>Venue</th><th>League</th><th>Place</th><th>Points</th></tr>
                  </thead>
                  <tbody>
                    {playerStats.recentGames.slice(0, 20).map((g, i) => (
                      <tr key={i}>
                        <td>{new Date(g.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                        <td>{g.venueName || '-'}</td>
                        <td>{g.leagueName || '-'}</td>
                        <td><strong>{g.place <= 3 ? ['🥇','🥈','🥉'][g.place-1] : `#${g.place}`}</strong></td>
                        <td>{g.pointsAwarded}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserEdit;
